import { useEffect } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, Quote, Star } from "lucide-react";
import { Logo } from "../../components/Logo";
import { Reveal } from "../../components/Reveal";
import { useLang } from "../../lib/i18n";
import { CASES, CASE_ORDER } from "./data";

const Eyebrow = ({ children }) => (
  <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-faint">
    <span className="h-1.5 w-1.5 rounded-full bg-amber" aria-hidden="true" />
    {children}
  </p>
);

export default function CasePage() {
  const { slug } = useParams();
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const item = CASES[slug];

  const c = item ? item[lang] : null;

  useEffect(() => {
    if (!c) return;
    const meta = document.querySelector('meta[name="description"]');
    const prevTitle = document.title;
    const prevDesc = meta?.getAttribute("content");
    document.title = c.meta.title;
    if (meta) meta.setAttribute("content", c.meta.description);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [slug, lang]);

  if (!item) return <Navigate to="/cases" replace />;

  const nextSlug = CASE_ORDER[(CASE_ORDER.indexOf(slug) + 1) % CASE_ORDER.length];
  const nextClient = CASES[nextSlug].client;
  const gridPhotos = item.photos.slice(1);

  return (
    <div className="min-h-screen bg-ink" data-testid="case-page">
      <header className="border-b border-white/10 bg-[#222222]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1" data-testid="case-lang-toggle">
              {["nl", "en"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`case-lang-${code}`}
                  className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    lang === code ? "bg-amber font-bold text-[#181510]" : "text-white/50 hover:text-white"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <Link to="/cases" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-amber" data-testid="case-back-link">
              <ArrowLeft size={13} />
              {lang === "nl" ? "Alle projecten" : "All projects"}
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-6xl px-6 pb-16 pt-14 md:pt-20" data-testid="case-hero">
          <Reveal>
            {item.logo.framed ? (
              <img src={item.logo.src} alt={item.logo.alt} className="h-14 w-14 rounded-[10px] border border-edge/60 object-cover" data-testid="case-logo" />
            ) : (
              <span className="inline-flex items-center rounded-[10px] border border-edge/60 bg-white px-3 py-2 shadow-sm">
                <img src={item.logo.src} alt={item.logo.alt} className="h-10 w-auto" data-testid="case-logo" />
              </span>
            )}
            <div className="mt-5">
              <Eyebrow>Project · {c.sector}</Eyebrow>
            </div>
            <h1 className="mt-5 max-w-3xl font-archivo text-4xl font-black leading-[1.05] tracking-tight text-cream sm:text-5xl" data-testid="case-title">
              {item.client}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-cream/85">{c.tagline}</p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-tan">{c.intro}</p>
            <div className="mt-7 flex flex-wrap gap-2" data-testid="case-tags">
              {c.servicesUsed.map((s, i) => (
                <span key={i} className="rounded-full border border-edge/70 bg-white px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/80" data-testid={`case-tag-${i}`}>
                  {s}
                </span>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <button
                onClick={() => navigate("/", { state: { scrollTo: "#gesprek" } })}
                className="group inline-flex items-center gap-2 rounded-[10px] bg-amber px-7 py-3.5 font-archivo text-sm font-bold text-[#181510] transition-colors duration-300 hover:bg-amberdeep"
                data-testid="case-cta-button"
              >
                {lang === "nl" ? "Plan een gratis kennismaking" : "Book a free intro call"}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-faint transition-colors hover:text-amberdeep"
                  data-testid="case-client-link"
                >
                  {lang === "nl" ? "Bezoek de website" : "Visit the website"}
                  <ExternalLink size={12} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 overflow-hidden rounded-[16px] border border-edge/40 shadow-xl shadow-black/10" data-testid="case-hero-image-wrap">
              <img
                src={item.heroPhoto.src}
                alt={item.heroPhoto.alt}
                className={`h-64 w-full object-cover sm:h-80 md:h-[420px] ${item.heroPhoto.zoom ? "scale-125" : ""}`}
                data-testid="case-hero-image"
              />
            </div>
            {item.video && (
              <video
                src={item.video}
                controls
                playsInline
                preload="metadata"
                className="mt-4 w-full rounded-[16px] border border-edge/40 bg-black shadow-xl shadow-black/10"
                data-testid="case-video"
              />
            )}
            {gridPhotos.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="case-gallery">
                {gridPhotos.map((p, i) => (
                  <div key={i} className="overflow-hidden rounded-[12px] border border-edge/40">
                    <img
                      src={p.src}
                      alt={p.alt}
                      loading="lazy"
                      className="h-40 w-full object-cover transition-transform duration-700 hover:scale-[1.05] md:h-48"
                      data-testid={`case-photo-${i}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </Reveal>
        </section>

        <section className="border-y border-edge/40 bg-[#F7F4EC]" data-testid="case-work">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <Reveal>
              <Eyebrow>{lang === "nl" ? "De aanpak" : "The approach"}</Eyebrow>
              <h2 className="mt-5 font-archivo text-3xl font-black tracking-tight text-cream">{c.workTitle}</h2>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {c.workItems.map((w, i) => (
                  <div key={i} className="rounded-[12px] border border-edge/60 bg-white p-6 transition-colors duration-300 hover:border-amber/60" data-testid={`case-work-${i}`}>
                    <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-amberdeep">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="mt-2 font-archivo text-lg font-extrabold tracking-tight text-cream">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-tan">{w.text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 py-16" data-testid="case-results">
          <Reveal>
            <Eyebrow>{lang === "nl" ? "Het resultaat" : "The result"}</Eyebrow>
            <h2 className="mt-5 font-archivo text-3xl font-black tracking-tight text-cream">{c.resultsTitle}</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {c.results.map((r, i) => (
                <div key={i} className="flex gap-4 rounded-[12px] border border-edge/60 bg-white p-6" data-testid={`case-result-${i}`}>
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber">
                    <Star size={13} className="fill-[#181510] text-[#181510]" />
                  </span>
                  <div>
                    <h3 className="font-archivo text-base font-extrabold tracking-tight text-cream">{r.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-tan">{r.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-16" data-testid="case-review">
          <Reveal>
            <div className="rounded-[16px] bg-[#181510] p-8 md:p-12">
              <Quote size={30} className="rotate-180 text-amber" aria-hidden="true" />
              <blockquote className="mt-5 max-w-3xl whitespace-pre-wrap text-sm leading-relaxed text-white/85 md:text-base" data-testid="case-review-quote">
                "{item.quote}"
              </blockquote>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1" data-testid="case-review-stars" aria-label="5/5">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="flex h-6 w-6 items-center justify-center rounded-[3px] bg-[#00b67a]">
                      <Star size={13} className="fill-white text-white" />
                    </span>
                  ))}
                </div>
                <span className="font-archivo text-sm font-bold text-white" data-testid="case-review-author">{item.client}</span>
              </div>
            </div>
          </Reveal>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-20" data-testid="case-cta">
          <Reveal>
            <div className="rounded-[16px] border border-edge/50 bg-[#F7F4EC] p-8 md:p-12">
              <h2 className="max-w-xl font-archivo text-3xl font-black tracking-tight text-cream">{c.ctaTitle}</h2>
              <p className="mt-3 max-w-xl leading-relaxed text-tan">{c.ctaText}</p>
              <div className="mt-7 flex flex-wrap items-center gap-5">
                <button
                  onClick={() => navigate("/", { state: { scrollTo: "#gesprek" } })}
                  className="group inline-flex items-center gap-2 rounded-[10px] bg-amber px-7 py-3.5 font-archivo text-sm font-bold text-[#181510] transition-colors duration-300 hover:bg-amberdeep"
                  data-testid="case-cta-button-bottom"
                >
                  {lang === "nl" ? "Plan een gratis kennismaking" : "Book a free intro call"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
                <Link
                  to={`/cases/${nextSlug}`}
                  className="group inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-faint transition-colors hover:text-amberdeep"
                  data-testid="case-next-link"
                >
                  {lang === "nl" ? "Volgend project" : "Next project"}: {nextClient}
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-edge/40 py-8">
        <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-faint" data-testid="case-footer">
          © 2026 4thscenery · Beyond The Ordinary
        </p>
      </footer>
    </div>
  );
}
